<template>
  <v-dialog
    :model-value="modelValue"
    max-width="723"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="rename-dialog-card">
      <div class="rename-dialog-content">
        <div class="rename-dialog-header">
          <span class="rename-dialog-title">Rename Chat</span>
          <v-btn
            icon
            size="40"
            variant="text"
            class="rename-dialog-close"
            aria-label="Close dialog"
            @click="$emit('update:modelValue', false)"
          >
            <v-icon size="40" aria-hidden="true"
              >mdi-close-circle-outline</v-icon
            >
          </v-btn>
        </div>
        <div class="rename-dialog-body">
          <v-text-field
            v-model="renameTitle"
            flat
            hide-details
            bg-color="#f9f9f9"
            label="Conversation title"
            variant="solo-filled"
            maxlength="255"
            autofocus
            @keyup.enter="confirmRename"
          ></v-text-field>
          <div class="rename-dialog-actions">
            <v-btn
              class="rename-dialog-cancel-btn"
              variant="outlined"
              @click="$emit('update:modelValue', false)"
            >
              Cancel
            </v-btn>
            <v-btn
              class="rename-dialog-confirm-btn"
              :loading="isRenaming"
              :disabled="!renameTitle.trim()"
              @click="confirmRename"
            >
              Rename
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { type Conversation } from '@/services/conversationService';
import { useConversationStore } from '@/stores/conversationStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { ref, watch } from 'vue';

const props = defineProps<{
  modelValue: boolean;
  conversation: Conversation | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  renamed: [];
}>();

const conversationStore = useConversationStore();
const notificationStore = useNotificationStore();

const renameTitle = ref('');
const isRenaming = ref(false);

watch(
  () => props.conversation,
  (conversation) => {
    renameTitle.value = conversation?.title ?? '';
  },
);

const confirmRename = async () => {
  if (!props.conversation || !renameTitle.value.trim()) return;

  try {
    isRenaming.value = true;
    await conversationStore.renameConversationById(
      props.conversation.id,
      renameTitle.value.trim(),
    );
    emit('update:modelValue', false);
    emit('renamed');
  } catch (error) {
    console.error('Failed to rename conversation:', error);
    notificationStore.notify(
      'Failed to rename conversation. Please try again.',
    );
  } finally {
    isRenaming.value = false;
  }
};
</script>

<style scoped lang="scss">
.rename-dialog-card {
  padding: 32px;
  border-radius: 24px !important;
  background-color: #fff;
}

.rename-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
}

.rename-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.rename-dialog-title {
  color: #303030;
  font-family: Poppins;
  font-size: 24px;
  font-weight: 600;
  line-height: normal;
}

.rename-dialog-close {
  color: #959595 !important;
}

.rename-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 48px;
}

::v-deep(.v-field) {
  border-radius: 12px;
}

::v-deep(.v-field-label) {
  color: #1e1e1e;
  font-family: Poppins;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  opacity: 1;
}

.rename-dialog-actions {
  display: flex;
  gap: 12px;
}

@media only screen and (width < $breakpoint-mobile) {
  .rename-dialog-actions {
    flex-direction: column;
  }
}

::v-deep(.rename-dialog-cancel-btn.v-btn) {
  flex: 1;
  min-height: 52px;
  border-radius: 8px;
  border-color: #959595;
  background-color: #fff;
  text-transform: none;

  .v-btn__content {
    color: #303030;
    font-family: Poppins;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: normal;
  }
}

::v-deep(.rename-dialog-confirm-btn.v-btn) {
  flex: 1;
  min-height: 52px;
  border-radius: 8px;
  background-color: #f77d1a;
  text-transform: none;

  .v-btn__content {
    color: #fff;
    font-family: Poppins;
    font-size: 16px;
    font-weight: 500;
  }
}
</style>
